/**
 * SoleadLab 2.0 — Share Session Store (F-02D)
 *
 * In-memory state for the view-only share-link visitor flow. The HttpOnly
 * `sl_share` cookie is the actual credential — it's set by the backend
 * and travels automatically on `credentials: 'include'` requests. This
 * store carries the metadata the UI needs to know about the session
 * (which site, what's exposed, has a passphrase been entered yet) and
 * the `active` flag that `apiFetch` reads to decide whether to skip
 * Bearer/X-Org-Id headers.
 *
 * Deliberate non-persistence:
 *   - No localStorage. The cookie is the persistence; if the visitor
 *     refreshes the page, SharePage re-resolves through the backend.
 *   - The raw token IS held in memory (so the SharePage can re-call
 *     unlock without re-prompting from the URL) but never written to
 *     storage.
 *   - The passphrase is NEVER stored — once unlock succeeds the cookie
 *     carries the unlocked flag.
 */

import { create } from 'zustand';

/** Safe metadata view returned by GET /api/v2/share/:token and POST /unlock. */
export interface ShareView {
  id: string;
  site_id: string;
  site_name: string | null;
  label: string | null;
  expires_at: string;
  status: 'active' | 'revoked';
  derived_status: 'active' | 'revoked' | 'expired';
  exposed_sections: string[];
  has_passphrase: boolean;
  requires_passphrase: boolean;
  view_count: number;
  last_viewed_at: string | null;
  unlocked?: boolean;
  /**
   * FL-1: 'field' unlocks the narrow indication write surface (notes /
   * status) in the viewer UI. Optional for backward compat — a missing
   * value is treated as 'view'. Enforcement is server-side; this only
   * drives which affordances render.
   */
  kind?: 'view' | 'field';
}

interface ShareSessionState {
  /**
   * Master switch read by apiFetch. `true` only after the visitor has
   * explicitly resolved + (if needed) unlocked a share link in the
   * current tab. Default `false` so a stray HttpOnly `sl_share` cookie
   * left over from an earlier visit doesn't silently flip a logged-in
   * user's app calls into share-mode.
   */
  active: boolean;

  /** Raw token in-memory (NOT persisted). Used by SharePage for unlock retries. */
  token: string | null;

  /** Resolved share view (id, site_id, exposed_sections, etc.). */
  view: ShareView | null;

  /** True iff the share-link has been unlocked (no passphrase OR correct passphrase entered). */
  unlocked: boolean;

  /** Convenience selectors for non-React callers (apiFetch). */
  isShareMode: () => boolean;
  hasSection: (section: string) => boolean;

  /** Mark the session after a successful resolve. */
  setFromResolve: (token: string, view: ShareView) => void;

  /** Mark the session unlocked after a successful POST /unlock. */
  setUnlocked: (view: ShareView) => void;

  /** Tear down state. Note: this CANNOT clear the HttpOnly sl_share cookie. */
  clearShareSession: () => void;
}

export const useShareSessionStore = create<ShareSessionState>()((set, get) => ({
  active: false,
  token: null,
  view: null,
  unlocked: false,

  isShareMode: () => get().active,

  hasSection: (section) => {
    const s = get();
    if (!s.active || !s.view) return false;
    if (section === 'core') return true;
    return s.view.exposed_sections.includes(section);
  },

  setFromResolve: (token, view) => {
    // "Active" only when the link is unlocked (no passphrase → unlocked,
    // passphrase required → not unlocked until POST /unlock succeeds).
    // The cookie is only issued by the backend when unlocked, so apiFetch
    // can't safely treat a not-yet-unlocked session as a share-mode call.
    const unlocked = !view.requires_passphrase;
    set({
      active: unlocked,
      token,
      view,
      unlocked,
    });
  },

  setUnlocked: (view) => {
    set({
      active: true,
      view,
      unlocked: true,
    });
  },

  clearShareSession: () => {
    set({ active: false, token: null, view: null, unlocked: false });
  },
}));
